import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { supabase } from '../lib/supabase'

interface Strategy {
  id: string
  user_id: string
  name: string
  version: number
  display_name: string
  description: string | null
  entry_rules: string | null
  exit_rules: string | null
  version_notes: string | null
  image_url: string | null
  is_public: boolean
  is_active: boolean
  is_locked: boolean
  created_at: string
  updated_at: string
}

interface CreateStrategyState {
  baseStrategy?: Strategy
}

export default function CreateStrategy() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { isPro, canCreateStrategyVersions, maxStrategies } = useMembership()

  const state = (location.state as CreateStrategyState | undefined) ?? {}
  const baseStrategy = state.baseStrategy
  const isCreatingVersion = !!baseStrategy

  const [strategies, setStrategies] = useState<Strategy[]>([])
  const [activeCount, setActiveCount] = useState(0)
  const [loading, setLoading] = useState(true)

  const [formName, setFormName] = useState(baseStrategy?.name ?? '')
  const [formDescription, setFormDescription] = useState(baseStrategy?.description ?? '')
  const [formEntryRules, setFormEntryRules] = useState(baseStrategy?.entry_rules ?? '')
  const [formExitRules, setFormExitRules] = useState(baseStrategy?.exit_rules ?? '')
  const [formVersionNotes, setFormVersionNotes] = useState('')
  const [formImage, setFormImage] = useState<File | null>(null)
  const [formImagePreview, setFormImagePreview] = useState<string | null>(baseStrategy?.image_url ?? null)
  const [formIsPublic, setFormIsPublic] = useState(baseStrategy?.is_public ?? false)
  const [formIsActive, setFormIsActive] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [showStrategyLimitModal, setShowStrategyLimitModal] = useState(false)

  useEffect(() => {
    if (!user) return

    const fetchStrategies = async () => {
      try {
        const { data, error } = await supabase
          .from('strategies')
          .select('*')
          .eq('user_id', user.id)
          .order('name', { ascending: true })
          .order('version', { ascending: true })

        if (error) throw error

        setStrategies(data || [])
        const active = (data || []).filter((s: Strategy) => s.is_active).length
        setActiveCount(active)
      } catch {
        setError('Failed to load strategies')
      } finally {
        setLoading(false)
      }
    }

    fetchStrategies()
  }, [user])

  const handleCreateStrategy = async () => {
    if (!user) return

    if (!formName.trim()) {
      setError('Strategy name is required')
      return
    }

    if (maxStrategies != null && formIsActive && activeCount >= maxStrategies) {
      setShowStrategyLimitModal(true)
      return
    }

    if (!canCreateStrategyVersions) {
      const existing = strategies.find((s) => s.name.toLowerCase() === formName.trim().toLowerCase())
      if (existing) {
        setError('A strategy with this name already exists. Your membership type does not allow creating versions. Please use a different name.')
        return
      }
    }

    try {
      setUploading(true)
      setError(null)

      let version = 0
      if (isPro) {
        const existingVersions = strategies.filter((s) => s.name.toLowerCase() === formName.trim().toLowerCase())
        if (existingVersions.length > 0) {
          version = Math.max(...existingVersions.map((s) => s.version)) + 1
        }
      }

      let imageUrl: string | null = null
      if (formImage && isPro) {
        const fileExt = formImage.name.split('.').pop()
        const fileName = `${user.id}/${Date.now()}.${fileExt}`
        const filePath = `strategy-images/${fileName}`

        const { error: uploadError } = await supabase.storage
          .from('strategy-images')
          .upload(filePath, formImage, { cacheControl: '3600', upsert: false })

        if (uploadError) throw uploadError

        const { data: urlData } = supabase.storage.from('strategy-images').getPublicUrl(filePath)
        imageUrl = urlData.publicUrl
      }

      const { error } = await supabase
        .from('strategies')
        .insert([
          {
            user_id: user.id,
            name: formName.trim(),
            version,
            description: formDescription.trim() || null,
            entry_rules: formEntryRules.trim() || null,
            exit_rules: formExitRules.trim() || null,
            version_notes: isCreatingVersion && formVersionNotes.trim() ? formVersionNotes.trim() : null,
            image_url: imageUrl,
            is_public: isPro ? formIsPublic : false,
            is_active: formIsActive,
            is_locked: false,
          },
        ])
        .select()
        .single()

      if (error) throw error

      if (version > 0) {
        const strategyNameToLock = formName.trim()
        await supabase
          .from('strategies')
          .update({ is_locked: true })
          .eq('user_id', user.id)
          .eq('name', strategyNameToLock)
          .lt('version', version)
      }

      const encodedName = encodeURIComponent(formName.trim())
      navigate(`/app/strategy/${encodedName}`, { replace: true })
    } catch (err: unknown) {
      setError((err as Error)?.message ?? 'Failed to create strategy')
    } finally {
      setUploading(false)
    }
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB')
        return
      }
      setFormImage(file)
      const reader = new FileReader()
      reader.onloadend = () => setFormImagePreview(reader.result as string)
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveImage = () => {
    setFormImage(null)
    setFormImagePreview(null)
  }

  if (!user) {
    navigate('/app/strategies', { replace: true })
    return null
  }

  if (loading) {
    return (
      <div className="w-full px-4 md:px-6 py-6">
        <p className="text-gray-500 text-center py-12">Loading...</p>
      </div>
    )
  }

  return (
    <div className="w-full px-4 md:px-6 py-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => navigate('/app/strategies', { replace: true })}
          className="text-gray-600 hover:text-gray-900 transition-colors p-1 -ml-1"
          aria-label="Back"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h2 className="text-xl font-semibold text-gray-900">
          {isCreatingVersion ? 'Create New Version' : 'Create Strategy'}
        </h2>
      </div>

      <div className="bg-app-box border border-gray-200 rounded-2xl shadow-sm p-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Strategy Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            placeholder="Enter strategy name"
          />
          {isPro && (
            <p className="text-xs text-gray-500 mt-1">Creating a strategy with an existing name will create a new version</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
          <textarea
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            placeholder="Describe your strategy..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Entry Rules</label>
          <textarea
            value={formEntryRules}
            onChange={(e) => setFormEntryRules(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            placeholder="Define entry conditions and rules..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Exit Rules</label>
          <textarea
            value={formExitRules}
            onChange={(e) => setFormExitRules(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            placeholder="Define exit conditions and rules..."
          />
        </div>

        {isCreatingVersion && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Version Notes</label>
            <textarea
              value={formVersionNotes}
              onChange={(e) => setFormVersionNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="What changed in this version?"
            />
          </div>
        )}

        {isPro && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Image (Optional)</label>
              {formImagePreview ? (
                <div className="relative">
                  <img src={formImagePreview} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 px-2 py-1 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              )}
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formIsPublic}
                  onChange={(e) => setFormIsPublic(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm text-gray-700">Make public</span>
              </label>
            </div>
          </>
        )}

        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formIsActive}
              onChange={(e) => setFormIsActive(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-gray-700">Active</span>
          </label>
        </div>

        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/app/strategies', { replace: true })}
            className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreateStrategy}
            disabled={uploading}
            className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-medium py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? (isCreatingVersion ? 'Creating Version...' : 'Creating...') : (isCreatingVersion ? 'Create Version' : 'Create Strategy')}
          </button>
        </div>
      </div>

      {/* Strategy limit reached modal */}
      {showStrategyLimitModal && maxStrategies != null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl bg-app-box border border-orange-200">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">Strategy limit reached</h2>
            <p className="text-sm text-gray-600 text-center mb-6">
              Your plan allows up to <strong>{maxStrategies}</strong> active strategies. Deactivate an existing strategy to add a new one, or upgrade to Pro for unlimited strategies.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => setShowStrategyLimitModal(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => navigate('/app/account')}
                className="flex-1 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
              >
                Upgrade to Pro
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
